import { isErr } from "@pacote/result"
import useSWR from "swr"
import { z } from "zod"

import { ListboxOption } from "@/components/ui/select-field"

import {
  ApiFailure,
  BaseApiErrorResponse,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

const countrySchema = z.object({
  description: z.string(),
  fiscalCode: z.string(),
  sign: z.string(),
  callingCodes: z.array(z.string()).optional(),
  actual: z.boolean(),
})

const countriesResponseSchema = baseApiResponseSchema.extend({
  countries: z.array(countrySchema),
})

type CountriesResponse = z.infer<typeof countriesResponseSchema>
type Country = z.infer<typeof countrySchema>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const countriesServiceEndpoint = "/residence-core-ms/v1/geo/countries"
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const countriesFetcher = async (key: string) => {
  const response = await baseClientFetcher<
    CountriesResponse,
    BaseApiErrorResponse
  >(
    {
      url: key,
      params: { current: false, enableItaly: true },
      successSchemaValidator: countriesResponseSchema,
    },
    { enrichRequestData: true },
  )

  // non 200 - 299 status
  if (isErr(response))
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `An error happened on request ${countriesServiceEndpoint}`,
      data: response.value.data,
      meta: response.value.meta,
      originalStatus: response.value.status,
    })

  const output: ListboxOption[] = []

  for (const country of response.value.countries) {
    const { callingCodes, sign } = country
    if (callingCodes) {
      for (const callingCode of callingCodes) {
        const optionId = `+${callingCode}`
        if (callingCode) {
          output.push({
            optionId,
            label: optionId,
            iconSrc: sign
              ? `/assets/countries-flags/${sign}.svg`
              : `/assets/countries-flags/- .svg`,
          })
        }
      }
    }
  }

  return output.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t.optionId === value.optionId),
  )
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useCountries = (key: string | null = countriesServiceEndpoint) =>
  useSWR(key, countriesFetcher)

export { useCountries }
export type { CountriesResponse, Country }
